/**
 * Query options for the My Cases task inbox (PTV2-1236).
 *
 * Backed by `GET /persons/tasks` — the caller's tasks across every person they
 * can see. Kept in its own feature slice (not `patientQueries`) because the
 * cache is keyed by the current user + filter, not by a patient guid.
 */

import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapTaskInboxItems } from '@/features/patient-record/mappers';
import type { TaskStatusCode } from '@/features/patient-record/api/tasks';
import type { TaskInboxItemViewModel } from '@/features/patient-record/types';
import { queryKeys } from '@/query/keys';

export type TaskInboxStatus = TaskStatusCode | 'all';

export interface TaskInboxParams {
  /** Assignee guid — defaults to the signed-in user at the call site. */
  readonly assignee: string;
  readonly status: TaskInboxStatus;
  readonly page: number;
  readonly size: number;
}

export interface TaskInboxPage {
  readonly items: readonly TaskInboxItemViewModel[];
  /** 0-based page number the BE returned. */
  readonly page: number;
  readonly size: number;
  readonly totalElements: number;
  readonly totalPages: number;
}

const STALE_TIME = 30_000;

export const myCasesQueries = {
  tasks: (params: TaskInboxParams) =>
    queryOptions({
      queryKey: [
        ...queryKeys.myCasesTasks,
        params.assignee,
        params.status,
        params.page,
        params.size,
      ] as const,
      queryFn: async ({ signal }): Promise<TaskInboxPage> => {
        const page = await apiClient.listTasks(
          {
            assignee: params.assignee,
            status: params.status,
            page: params.page,
            size: params.size,
          },
          { signal },
        );
        const items = mapTaskInboxItems(page);
        return {
          items,
          page: page.number ?? params.page,
          size: page.size ?? params.size,
          totalElements: page.totalElements ?? items.length,
          totalPages:
            page.totalPages ??
            Math.max(
              1,
              Math.ceil((page.totalElements ?? items.length) / Math.max(params.size, 1)),
            ),
        };
      },
      enabled: params.assignee.length > 0,
      placeholderData: keepPreviousData,
      staleTime: STALE_TIME,
    }),
};
